import React, { useMemo, useRef, useState } from 'react';
import {
	Autocomplete,
	Box,
	Chip,
	CircularProgress,
	ClickAwayListener,
	ListItemIcon,
	TextField,
	Typography,
	createFilterOptions,
	useTheme,
} from '@mui/material';
import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import { minimalInputTheme } from '@/app/theme';
import { useChipFit } from '@/hooks/useChipFit';

/** Value emitted, in single mode, when the "All" option is chosen. */
export const ALL_OPTION_ID = 'all';

const ALL_OPTION = { id: ALL_OPTION_ID, name: 'All' };
const CLEAR_OPTION = { id: 'clear', name: 'Clear Filter' };
// Last row when optionLimit cuts the list short; not selectable.
const MORE_OPTION_ID = 'more';
const isHelperOption = (option) =>
	[ALL_OPTION.id, CLEAR_OPTION.id, MORE_OPTION_ID].includes(option.id);

// With fitMenuToContent, the menu grows to fit its options but is never narrower than the
// field. The minimum comes from a Popper.js modifier, since CSS can't read the field's width.
const FIT_CONTENT_POPPER_PROPS = {
	sx: { width: 'max-content !important', maxWidth: '90vw' },
	modifiers: [
		{
			name: 'minWidthOfField',
			enabled: true,
			phase: 'beforeWrite',
			requires: ['computeStyles'],
			fn: ({ state }) => {
				state.styles.popper.minWidth = `${state.rects.reference.width}px`;
			},
		},
	],
};

const toOption = (option) =>
	typeof option === 'object' ? option : { id: option, name: String(option) };

/**
 * The select UI shared by the owner, broker group and admin pickers. It holds no data:
 * callers pass the options and map ids to whatever they need.
 *
 * - options: objects with an `id` (labelled by getOptionLabel), or plain strings/numbers,
 *   which become { id, name }. Options are shown in the order given.
 * - value: single mode, an option id or null (ALL_OPTION_ID for "All"); multiple mode, an
 *   array of option ids. onChange emits the same shape.
 * - showAllOption: an "All" option. In multiple mode it selects every option that isn't
 *   disabled, and is hidden once they're all selected.
 * - showClearOption: multiple mode only, a "Clear Filter" option that removes everything
 *   except fixed options.
 * - isOptionFixed: multiple mode, options that can't be removed. isOptionDisabled: options
 *   that can't be chosen.
 * - renderOptionContent / renderSingleValue: custom content for a dropdown row and for the
 *   chosen value in single mode (e.g. an avatar). getChipProps: extra props for each chip
 *   in multiple mode.
 * - searchable: let the user type to filter the options. Off by default, which keeps the
 *   input read-only like the CRM selects. filterOptions changes how typing matches.
 * - optionLimit: with searchable, show at most this many matching options, plus a row
 *   saying how many matched, so long lists stay quick to open (and only the shown options
 *   render, e.g. fetch their avatars). Typing narrows the matches.
 * - fitMenuToContent: the dropdown grows to fit its options (e.g. full names) instead of
 *   matching the field's width, and is never narrower than the field.
 * Other props go to the MUI Autocomplete.
 */
export const ResponsiveAutocomplete = ({
	options = [],
	value,
	onChange,
	multiple = false,
	disabled,
	loading = false,
	minimal = false,
	error = false,
	onBlur,
	disableClearable = false,
	groupBy,
	showCheckbox = false,
	showAllOption = false,
	showClearOption = false,
	isOptionDisabled = () => false,
	isOptionFixed = () => false,
	getOptionLabel = (option) => option.name ?? '',
	renderOptionContent,
	renderSingleValue,
	getChipProps = () => ({}),
	searchable = false,
	filterOptions,
	optionLimit,
	fitMenuToContent = false,
	sx,
	...props
}) => {
	const theme = useTheme();
	const containerRef = useRef();
	const [open, setOpen] = useState(false);

	const filterWithLimit = (allOptions, state) => {
		const filtered = (filterOptions ?? createFilterOptions({ stringify: getOptionLabel }))(
			allOptions,
			state,
		);
		const matches = filtered.filter((option) => !isHelperOption(option));
		if (!optionLimit || matches.length <= optionLimit) {
			return filtered;
		}
		return [
			...filtered.filter(isHelperOption),
			...matches.slice(0, optionLimit),
			{
				id: MORE_OPTION_ID,
				name: `Showing ${optionLimit} of ${matches.length}. Type to narrow the list.`,
			},
		];
	};

	const normalisedOptions = useMemo(() => options.map(toOption), [options]);
	const selectedIds = multiple ? (value ?? []) : [];

	const selected = multiple
		? normalisedOptions.filter((option) => selectedIds.includes(option.id))
		: ((showAllOption && value === ALL_OPTION_ID && ALL_OPTION) ||
			normalisedOptions.find((option) => option.id === value) ||
			null);

	const selectableOptions = normalisedOptions.filter((option) => !isOptionDisabled(option));
	const fixedIds = multiple
		? selected.filter((option) => isOptionFixed(option)).map((option) => option.id)
		: [];
	const isAllSelected =
		multiple &&
		showAllOption &&
		selectableOptions.length > 0 &&
		selectableOptions.every((option) => selectedIds.includes(option.id));

	const helperOptions = multiple
		? [
			...(showAllOption && !isAllSelected ? [ALL_OPTION] : []),
			...(showClearOption && selected.length > fixedIds.length ? [CLEAR_OPTION] : []),
		]
		: [...(showAllOption ? [ALL_OPTION] : [])];

	const limitTags = useChipFit(containerRef, multiple ? selected.length : 0);

	const handleChange = (_, next) => {
		if (!multiple) {
			onChange(next?.id ?? null);
			return;
		}
		const nextIds = next.map((option) => option.id);
		if (nextIds.includes(CLEAR_OPTION.id)) {
			onChange(fixedIds);
			return;
		}
		if (nextIds.includes(ALL_OPTION.id)) {
			onChange([...new Set([...selectableOptions.map((option) => option.id), ...fixedIds])]);
			return;
		}
		onChange([...new Set([...nextIds, ...fixedIds])]);
	};

	return (
		<ClickAwayListener onClickAway={() => setOpen(false)}>
			<Box sx={{ position: 'relative' }}>
				<Autocomplete
					ref={containerRef}
					fullWidth
					open={open}
					onOpen={() => setOpen(true)}
					onClose={(_, reason) => {
						// Multiple mode stays open while options are picked and unpicked
						if (multiple && ['selectOption', 'removeOption'].includes(reason)) return;
						setOpen(false);
					}}
					options={[...helperOptions, ...normalisedOptions]}
					// Key rows by id: the default key is the label, and duplicate labels (two users with
					// the same name, or none) make React mix up the rows.
					getOptionKey={(option) => option.id}
					value={selected}
					onChange={handleChange}
					multiple={multiple}
					disabled={disabled}
					loading={loading}
					onBlur={onBlur}
					disableClearable={disableClearable}
					isOptionEqualToValue={(option, selectedOption) => option.id === selectedOption.id}
					getOptionLabel={getOptionLabel}
					{...(filterOptions && { filterOptions })}
					{...(searchable && optionLimit && { filterOptions: filterWithLimit })}
					getOptionDisabled={(option) =>
						option.id === MORE_OPTION_ID ||
						(!isHelperOption(option) &&
							(isOptionDisabled(option) || (multiple && isOptionFixed(option))))
					}
					{...(groupBy && {
						groupBy: (option) => (isHelperOption(option) ? '' : option[groupBy]),
					})}
					limitTags={limitTags}
					renderOption={(liProps, option, { selected: isSelected }) => {
						const { key, ...optionProps } = liProps;
						if (option.id === MORE_OPTION_ID) {
							return (
								<li key={key} {...optionProps} style={{ width: '100%' }}>
									<Typography variant='body2' color='text.secondary'>
										{option.name}
									</Typography>
								</li>
							);
						}
						const checked = isSelected || (option.id === ALL_OPTION.id && isAllSelected);
						const Icon = checked ? CheckBox : CheckBoxOutlineBlank;
						return (
							<li key={key} {...optionProps} style={{ width: '100%' }}>
								{showCheckbox && (
									<ListItemIcon sx={{ minWidth: '2.25em' }}>
										<Icon
											sx={{ ...(checked && { color: theme.palette.primary.main }) }}
										/>
									</ListItemIcon>
								)}
								{renderOptionContent && !isHelperOption(option) ? (
									renderOptionContent(option)
								) : (
									<Typography>{getOptionLabel(option)}</Typography>
								)}
							</li>
						);
					}}
					{...(multiple && {
						renderValue: (chosen, getItemProps) =>
							chosen.map((option, index) => {
								const { key, ...itemProps } = getItemProps({ index });
								return (
									<Chip
										key={key}
										size='small'
										label={getOptionLabel(option)}
										{...itemProps}
										{...getChipProps(option)}
										disabled={isOptionFixed(option)}
									/>
								);
							}),
					})}
					{...(!multiple &&
				renderSingleValue && {
					// Clicks pass through to the input so clicking the value opens the dropdown
						renderValue: (option) => (
							<Box sx={{ display: 'contents', pointerEvents: 'none' }}>
								{renderSingleValue(option)}
							</Box>
						),
					})}
					renderInput={(params) => (
						<TextField
							{...params}
							variant={minimal ? 'standard' : 'outlined'}
							error={error}
							{...(!searchable && { onKeyDown: (event) => event.preventDefault() })}
							slotProps={{
								input: {
									...params.InputProps,
									...(!searchable && {
										readOnly: true,
										style: { caretColor: 'transparent', cursor: 'pointer' },
									}),
									endAdornment: (
										<>
											{loading && <CircularProgress color='inherit' size={20} />}
											{params.InputProps.endAdornment}
										</>
									),
								},
							}}
							sx={{
								...(!searchable && { cursor: 'pointer' }),
								...(!searchable &&
							multiple &&
							selected.length > 0 && { '& input': { display: 'none' } }),
								...(minimal && {
									...minimalInputTheme,
									'& .MuiInputBase-root': { backgroundColor: 'transparent' },
								}),
							}}
						/>
					)}
					{...(fitMenuToContent && { slotProps: { popper: FIT_CONTENT_POPPER_PROPS } })}
					{...props}
					sx={{
						...(!searchable && { cursor: 'pointer', '& input': { cursor: 'pointer' } }),
						'& .MuiInputBase-root': {
							...(!searchable && { cursor: 'pointer' }),
							// A custom single value needs room for its end adornment; chips and plain
							// text stay on one line.
							...(!multiple && renderSingleValue
								? { paddingRight: '9px !important' }
								: { py: 0, flexWrap: 'nowrap', overflow: 'hidden' }),
						},
						'& .MuiAutocomplete-tag': { maxWidth: '100%' },
						...sx,
					}}
				/>
			</Box>
		</ClickAwayListener>
	);
};

ResponsiveAutocomplete.displayName = 'ResponsiveAutocomplete';
